import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";

import {
  createAdminSchool,
  deleteAdminSchool,
  fetchAdminSchoolFilters,
  fetchAdminSchools,
  fetchAdminSchoolStats,
  updateAdminSchool,
} from "../../../services/adminApi";
import { useToast } from "../../../hook/useToast";
import AdminDashboardLayout from "../AdminDashboardLayout";
import StatsCard from "../StatsCard";
import AddSchoolModal from "./AddSchoolModal";

const DEFAULT_PAGE_SIZE = 20;
const NAVY = "#292E5C";
const ORANGE = "#FFA54D";

const EMPTY_FILTERS = {
  city: "",
  pincode: "",
  board: "",
  status: "",
};

const DEFAULT_STATUS_OPTIONS = [
  { value: "pilot", label: "Pilot" },
  { value: "paid", label: "Paid" },
];

const COLUMNS = [
  { id: "sr", label: "Sr No.", width: 78, align: "left" },
  { id: "school_name", label: "School name", width: 270, align: "left" },
  { id: "city", label: "City", width: 180, align: "left" },
  { id: "pincode", label: "Pin code", width: 140, align: "left" },
  { id: "board", label: "Board", width: 140, align: "left" },
  { id: "status", label: "Status", width: 140, align: "center" },
  { id: "number_of_students", label: "Number of Students", width: 190, align: "center" },
  { id: "actions", label: "Actions", width: 120, align: "center" },
];

const headerTextSx = {
  color: "#667085",
  fontSize: 12,
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  lineHeight: "18px",
  whiteSpace: "nowrap",
};

const bodyTextSx = {
  color: "#344054",
  fontSize: 13,
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
  lineHeight: "18px",
};

const schoolNameSx = {
  color: "#101828",
  fontSize: 13,
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 500,
  lineHeight: "20px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const tableCellSx = {
  height: 72,
  px: 1.25,
  py: 2,
  borderBottom: "1px solid #EAECF0",
};

const selectSx = {
  width: 155,
  height: 36,
  borderRadius: "7px",
  bgcolor: "#fff",
  color: "#888888",
  fontSize: 12,
  fontFamily: "Montserrat, sans-serif",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DCDFDE" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#DCDFDE" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CEDA" },
  "& .MuiSelect-select": {
    py: 0,
    pl: 1.25,
    pr: 3,
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
};

const searchSx = {
  width: 245,
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "7px",
    bgcolor: "#fff",
    fontSize: 12,
    fontFamily: "Montserrat, sans-serif",
    "& fieldset": { borderColor: "#E2ECF3" },
    "&:hover fieldset": { borderColor: "#E2ECF3" },
    "&.Mui-focused fieldset": { borderColor: "#C8CEDA" },
  },
  "& .MuiInputBase-input": {
    color: "#292948",
    "&::placeholder": {
      color: "#888888",
      opacity: 1,
    },
  },
};

const filterSelectSx = {
  height: 34,
  width: "100%",
  borderRadius: "7px",
  bgcolor: "#fff",
  color: "#171725",
  fontSize: 12,
  fontFamily: "Montserrat, sans-serif",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2ECF3" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D7DAE5" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CEDA" },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
  },
};

const filterMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      maxHeight: 210,
      borderRadius: "8px",
      border: "1px solid #EFEFF4",
      boxShadow: "0 14px 34px rgba(45, 49, 95, 0.14)",
      "& .MuiMenuItem-root": {
        minHeight: 30,
        fontSize: 12,
        whiteSpace: "normal",
      },
    },
  },
};

const formatNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return "--";
  }

  const number = Number(value);
  return Number.isNaN(number) ? value : number.toLocaleString("en-IN");
};

const normalizeOptions = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option) => {
      if (option && typeof option === "object") {
        const value = option.value ?? option.id ?? option.key ?? option.label ?? "";
        const label = option.label ?? option.name ?? option.value ?? "";

        return {
          value: String(value),
          label: String(label || value),
        };
      }

      return {
        value: String(option ?? ""),
        label: String(option ?? ""),
      };
    })
    .filter((option) => option.value && option.label);
};

const getPaginationItems = (page, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);

  if (start > 2) {
    items.push("dots-start");
  }

  for (let index = start; index <= end; index += 1) {
    items.push(index);
  }

  if (end < totalPages - 1) {
    items.push("dots-end");
  }

  items.push(totalPages);
  return items;
};

const normalizeStatus = (value) => {
  const status = String(value || "Pilot").trim();
  return status || "Pilot";
};

const areFiltersEmpty = (filters) => {
  return Object.values(filters).every((value) => !value);
};

const extractApiErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }

  const errors = responseData?.errors || responseData?.data || responseData;

  if (typeof errors === "string" && errors.trim()) {
    return errors;
  }

  if (errors && typeof errors === "object") {
    const firstValue = Object.values(errors).flat().find(Boolean);

    if (typeof firstValue === "string") {
      return firstValue;
    }
  }

  return fallback;
};

const StatusBadge = ({ status }) => {
  const label = normalizeStatus(status);
  const isPaid = label.toLowerCase() === "paid";

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 24,
        px: 0.5,
        borderRadius: "24px",
        border: isPaid ? "1px solid #B7E4C7" : "1px solid #D1D5DB",
        bgcolor: isPaid ? "#ECFDF3" : "#F3F4F6",
        color: isPaid ? "#067647" : "rgba(17, 24, 39, 0.70)",
        fontSize: 11,
        fontFamily: "Inter, sans-serif",
        fontWeight: 500,
        lineHeight: "16px",
        "& .MuiChip-label": {
          px: 1,
        },
      }}
    />
  );
};

StatusBadge.propTypes = {
  status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const SchoolsPage = () => {
  const { showToast } = useToast();
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    pincodes: [],
    boards: [],
    statuses: DEFAULT_STATUS_OPTIONS,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsError, setSchoolsError] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deletingSchoolId, setDeletingSchoolId] = useState(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await fetchAdminSchoolStats();
      setStats(response?.data?.data || {});
    } catch {
      showToast("Failed to load school stats", "error");
    } finally {
      setStatsLoading(false);
    }
  }, [showToast]);

  const loadFilterOptions = useCallback(async () => {
    setFiltersLoading(true);

    try {
      const response = await fetchAdminSchoolFilters();
      const data = response?.data?.data || {};
      const statuses = normalizeOptions(data.statuses);

      setFilterOptions({
        cities: normalizeOptions(data.cities),
        pincodes: normalizeOptions(data.pincodes),
        boards: normalizeOptions(data.boards),
        statuses: statuses.length ? statuses : DEFAULT_STATUS_OPTIONS,
      });
    } catch {
      showToast("Failed to load school filters", "error");
    } finally {
      setFiltersLoading(false);
    }
  }, [showToast]);

  const loadSchools = useCallback(async () => {
    setSchoolsLoading(true);
    setSchoolsError("");

    try {
      const response = await fetchAdminSchools({
        page,
        pageSize,
        search: debouncedSearch,
        filters: appliedFilters,
      });
      const data = response?.data?.data || {};

      setSchools(Array.isArray(data.schools) ? data.schools : []);
      setPagination({
        page: data.pagination?.page || page,
        page_size: data.pagination?.page_size || pageSize,
        total_records: data.pagination?.total_records || 0,
        total_pages: data.pagination?.total_pages || 0,
      });
    } catch {
      setSchools([]);
      setSchoolsError("Unable to load schools right now.");
      showToast("Failed to load schools", "error");
    } finally {
      setSchoolsLoading(false);
    }
  }, [appliedFilters, debouncedSearch, page, pageSize, showToast]);

  useEffect(() => {
    loadStats();
    loadFilterOptions();
  }, [loadFilterOptions, loadStats]);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  const totalPages = Math.max(1, pagination.total_pages || 1);
  const totalRecords = pagination.total_records || 0;
  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;
  const hasDraftFilters = Object.values(draftFilters).some(Boolean);
  const isFilterOpen = Boolean(filterAnchor);
  const paginationItems = useMemo(
    () => getPaginationItems(page, totalPages),
    [page, totalPages]
  );

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleOpenFilter = (event) => {
    setDraftFilters(appliedFilters);
    setFilterAnchor(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setDraftFilters(appliedFilters);
    setFilterAnchor(null);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setFilterAnchor(null);
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
    setFilterAnchor(null);
  };

  const handleOpenAddSchool = () => {
    setEditingSchool(null);
    setAddModalOpen(true);
  };

  const handleEditSchool = (school) => {
    setEditingSchool(school);
    setAddModalOpen(true);
  };

  const handleCloseSchoolModal = () => {
    if (!creating) {
      setAddModalOpen(false);
      setEditingSchool(null);
    }
  };

  const handleSubmitSchool = async (payload) => {
    setCreating(true);

    try {
      if (editingSchool?.school_id) {
        await updateAdminSchool(editingSchool.school_id, payload);
        showToast("School updated successfully", "success");
      } else {
        await createAdminSchool(payload);
        showToast("School added successfully", "success");
      }

      await loadStats();
      await loadFilterOptions();

      if (editingSchool?.school_id) {
        await loadSchools();
      } else {
        const isDefaultView =
          page === 1 &&
          !search.trim() &&
          !debouncedSearch &&
          areFiltersEmpty(appliedFilters);

        if (isDefaultView) {
          await loadSchools();
        } else {
          setSearch("");
          setDebouncedSearch("");
          setDraftFilters(EMPTY_FILTERS);
          setAppliedFilters(EMPTY_FILTERS);
          setPage(1);
        }
      }

      return { ok: true };
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        editingSchool?.school_id ? "Failed to update school." : "Failed to add school."
      );
      showToast(message, "error");
      return { ok: false, message };
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSchool = async (school) => {
    if (!school?.school_id) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${school.school_name || "this school"}? This will remove it from the admin school table.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingSchoolId(school.school_id);

    try {
      await deleteAdminSchool(school.school_id);
      showToast("School deleted successfully", "success");
      await loadStats();
      await loadFilterOptions();

      if (schools.length === 1 && page > 1) {
        setPage((current) => Math.max(1, current - 1));
      } else {
        await loadSchools();
      }
    } catch (error) {
      const message = extractApiErrorMessage(error, "Failed to delete school.");
      showToast(message, "error");
    } finally {
      setDeletingSchoolId(null);
    }
  };

  const renderFilterSelect = (key, label, placeholder, options) => {
    const normalizedOptions = normalizeOptions(options);

    return (
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#4A4F63", mb: 0.45 }}>
          {label}
        </Typography>
        <Select
          fullWidth
          displayEmpty
          disabled={filtersLoading}
          value={draftFilters[key]}
          IconComponent={KeyboardArrowDownIcon}
          onChange={(event) => handleFilterChange(key, event.target.value)}
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography sx={{ fontSize: 12, color: "#92929D" }}>
                  {placeholder}
                </Typography>
              );
            }

            return normalizedOptions.find((option) => option.value === value)?.label || value;
          }}
          MenuProps={filterMenuProps}
          sx={filterSelectSx}
        >
          <MenuItem value="">All</MenuItem>
          {normalizedOptions.map((option) => (
            <MenuItem key={`${key}-${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  return (
    <AdminDashboardLayout>
      <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: { xs: 0, md: 0 } }}>
        <Typography
          sx={{
            color: "#171717",
            fontSize: 21,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            lineHeight: "24px",
            mb: 1.4,
          }}
        >
          School Records
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            mb: 2.2,
          }}
        >
          <StatsCard
            value={statsLoading ? "--" : formatNumber(stats?.total_schools)}
            label="Total Schools"
            icon="total"
            bgColor="rgba(11, 34, 86, 0.04)"
          />
          <StatsCard
            value={statsLoading ? "--" : formatNumber(stats?.schools_this_month)}
            label="Schools this month"
            icon="month"
            bgColor="rgba(247, 170, 71, 0.08)"
          />
          <StatsCard
            value={statsLoading ? "--" : formatNumber(stats?.schools_this_year)}
            label="Schools this year"
            icon="year"
            bgColor="#F3F7FF"
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: "10px",
            bgcolor: "#fff",
            border: "1px solid #EFEFF4",
            borderRadius: "7px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", lg: "center" },
              gap: 1,
              flexDirection: { xs: "column", lg: "row" },
            }}
          >
            <Typography
              sx={{
                color: "#000",
                fontSize: 18,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 500,
                lineHeight: "24px",
              }}
            >
              School Records
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                gap: "5px",
                flexWrap: "wrap",
              }}
            >
              <Select
                displayEmpty
                value={pageSize}
                onChange={handlePageSizeChange}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(value) => `${value} Records`}
                sx={selectSx}
              >
                <MenuItem value={10}>10 Records</MenuItem>
                <MenuItem value={20}>20 Records</MenuItem>
                <MenuItem value={50}>50 Records</MenuItem>
                <MenuItem value={100}>100 Records</MenuItem>
              </Select>

              <TextField
                placeholder="Search..."
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={searchSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {schoolsLoading && search.trim() !== debouncedSearch ? (
                        <CircularProgress size={14} sx={{ color: NAVY }} />
                      ) : (
                        <SearchIcon sx={{ fontSize: 16, color: "#585874" }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="button"
                variant="outlined"
                startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
                onClick={handleOpenFilter}
                sx={{
                  height: 36,
                  px: 1.25,
                  borderRadius: "7px",
                  borderColor: activeFilterCount ? ORANGE : "#E2ECF3",
                  bgcolor: activeFilterCount ? "#FFF7ED" : "#fff",
                  color: activeFilterCount ? "#B54708" : "#363636",
                  textTransform: "none",
                  fontSize: 12,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                  lineHeight: "15px",
                  "&:hover": {
                    borderColor: activeFilterCount ? ORANGE : "#D7DAE5",
                    bgcolor: activeFilterCount ? "#FFF2E0" : "#fff",
                  },
                }}
              >
                Filter
              </Button>

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={handleOpenAddSchool}
                sx={{
                  height: 36,
                  px: 1.5,
                  borderRadius: "5px",
                  bgcolor: ORANGE,
                  color: "#fff",
                  textTransform: "none",
                  fontSize: 12,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  lineHeight: "14px",
                  boxShadow:
                    "0px 1px 2px rgba(16, 24, 40, 0.04), 0px 1px 2px rgba(16, 24, 40, 0.04)",
                  "&:hover": {
                    bgcolor: "#F59A3D",
                    boxShadow:
                      "0px 1px 2px rgba(16, 24, 40, 0.04), 0px 1px 2px rgba(16, 24, 40, 0.04)",
                  },
                }}
              >
                Add School
              </Button>
            </Box>
          </Box>

          <Popover
            open={isFilterOpen}
            anchorEl={filterAnchor}
            onClose={handleCloseFilter}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                width: 280,
                maxWidth: "calc(100vw - 32px)",
                borderRadius: "10px",
                border: "1px solid #EFEFF4",
                boxShadow: "0 18px 48px rgba(45, 49, 95, 0.16)",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                px: 1.4,
                py: 1.05,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #EFEFF4",
                bgcolor: "#FAFBFD",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#171725" }}>
                  Filter Schools
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#92929D", mt: 0.15 }}>
                  Choose any field and apply
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleCloseFilter}>
                <CloseIcon sx={{ fontSize: 16, color: "#4A4F63" }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 1.4, display: "grid", gap: 0.9 }}>
              {renderFilterSelect("city", "City", "Select city", filterOptions.cities)}
              {renderFilterSelect(
                "pincode",
                "Pin code",
                "Select pin code",
                filterOptions.pincodes
              )}
              {renderFilterSelect("board", "Board", "Select board", filterOptions.boards)}
              {renderFilterSelect(
                "status",
                "Status",
                "Select status",
                filterOptions.statuses
              )}
            </Box>

            <Box
              sx={{
                px: 1.4,
                py: 1.05,
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.8,
                borderTop: "1px solid #EFEFF4",
                bgcolor: "#fff",
              }}
            >
              <Button
                startIcon={<RestartAltIcon sx={{ fontSize: 17 }} />}
                onClick={handleClearFilters}
                disabled={schoolsLoading || (!activeFilterCount && !hasDraftFilters)}
                sx={{
                  height: 30,
                  borderRadius: "7px",
                  color: NAVY,
                  border: "1px solid #E4E6EF",
                  bgcolor: "#fff",
                  textTransform: "none",
                  fontSize: 12,
                  px: 1.2,
                  "&:hover": { bgcolor: "#F5F6FA", borderColor: "#D7DAE5" },
                }}
              >
                Clear
              </Button>

              <Button
                startIcon={<FilterListIcon sx={{ fontSize: 17 }} />}
                onClick={handleApplyFilters}
                disabled={schoolsLoading}
                sx={{
                  height: 30,
                  borderRadius: "7px",
                  bgcolor: NAVY,
                  color: "#fff",
                  textTransform: "none",
                  fontSize: 12,
                  px: 1.35,
                  "&:hover": { bgcolor: "#252957" },
                  "&.Mui-disabled": {
                    bgcolor: "#D5D7E2",
                    color: "#fff",
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Popover>

          {schoolsError ? (
            <Alert severity="error" sx={{ borderRadius: "7px" }}>
              {schoolsError}
            </Alert>
          ) : null}

          <TableContainer
            sx={{
              position: "relative",
              minHeight: 420,
              bgcolor: "#fff",
              borderRadius: "7px",
              border: "1px solid #DCDFDE",
              overflow: "auto",
            }}
          >
            {schoolsLoading ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.72)",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={30} sx={{ color: NAVY }} />
              </Box>
            ) : null}

            <Table sx={{ minWidth: 1258 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F9FAFB", height: 44 }}>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        width: column.width,
                        minWidth: column.width,
                        height: 44,
                        px: 1.25,
                        py: 1.5,
                        borderBottom: "1px solid #EAECF0",
                      }}
                    >
                      <Typography sx={headerTextSx}>{column.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {!schoolsLoading && schools.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 7 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#171725" }}>
                        No schools found
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.6 }}>
                        {debouncedSearch || activeFilterCount
                          ? "Try changing the search or filters."
                          : "Schools will appear here after they are added."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  schools.map((school, index) => {
                    const serialNumber = (page - 1) * pageSize + index + 1;
                    const schoolName = school.school_name || "--";

                    return (
                      <TableRow
                        key={school.school_id || serialNumber}
                        hover
                        sx={{
                          height: 72,
                          "&:hover": { bgcolor: "#FBFCFF" },
                        }}
                      >
                        <TableCell sx={tableCellSx}>
                          <Typography
                            sx={{
                              color: "#101828",
                              fontSize: 13,
                              fontFamily: "Poppins, sans-serif",
                              fontWeight: 400,
                              lineHeight: "20px",
                            }}
                          >
                            {serialNumber}
                          </Typography>
                        </TableCell>

                        <TableCell sx={tableCellSx}>
                          <Typography title={schoolName} sx={schoolNameSx}>
                            {schoolName}
                          </Typography>
                        </TableCell>

                        <TableCell sx={tableCellSx}>
                          <Typography title={school.city || ""} sx={bodyTextSx}>
                            {school.city || "--"}
                          </Typography>
                        </TableCell>

                        <TableCell sx={tableCellSx}>
                          <Typography sx={bodyTextSx}>{school.pincode || "--"}</Typography>
                        </TableCell>

                        <TableCell sx={tableCellSx}>
                          <Typography sx={bodyTextSx}>{school.board || "--"}</Typography>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <StatusBadge status={school.status} />
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <Typography sx={bodyTextSx}>
                            {school.number_of_students === null ||
                            school.number_of_students === undefined ||
                            school.number_of_students === ""
                              ? "0"
                              : formatNumber(school.number_of_students)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center" sx={tableCellSx}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              aria-label={`Edit ${schoolName}`}
                              size="small"
                              disabled={schoolsLoading || deletingSchoolId === school.school_id}
                              onClick={() => handleEditSchool(school)}
                              sx={{
                                width: 30,
                                height: 30,
                                color: NAVY,
                                bgcolor: "rgba(41, 46, 92, 0.06)",
                                "&:hover": { bgcolor: "rgba(41, 46, 92, 0.12)" },
                              }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 17 }} />
                            </IconButton>

                            <IconButton
                              aria-label={`Delete ${schoolName}`}
                              size="small"
                              disabled={schoolsLoading || deletingSchoolId === school.school_id}
                              onClick={() => handleDeleteSchool(school)}
                              sx={{
                                width: 30,
                                height: 30,
                                color: "#D92D20",
                                bgcolor: "rgba(217, 45, 32, 0.06)",
                                "&:hover": { bgcolor: "rgba(217, 45, 32, 0.12)" },
                              }}
                            >
                              {deletingSchoolId === school.school_id ? (
                                <CircularProgress size={15} sx={{ color: "#D92D20" }} />
                              ) : (
                                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              minHeight: 36,
            }}
          >
            <IconButton
              aria-label="Previous page"
              disabled={page <= 1 || schoolsLoading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              sx={{
                width: 28,
                height: 28,
                color: "#6E6893",
                borderRadius: "7px",
              }}
            >
              <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {paginationItems.map((item) => {
              if (typeof item === "string") {
                return (
                  <Typography
                    key={item}
                    sx={{
                      color: "#292948",
                      fontSize: 14,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      lineHeight: "26px",
                    }}
                  >
                    ..........
                  </Typography>
                );
              }

              const isActive = item === page;

              return (
                <Button
                  key={item}
                  disabled={schoolsLoading}
                  onClick={() => setPage(item)}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    height: 32,
                    p: 0,
                    borderRadius: "999px",
                    color: isActive ? "#F1EEF9" : "#292948",
                    bgcolor: isActive ? NAVY : "transparent",
                    fontSize: 14,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    lineHeight: "26px",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: isActive ? NAVY : "rgba(41, 46, 92, 0.06)",
                    },
                  }}
                >
                  {item}
                </Button>
              );
            })}

            <IconButton
              aria-label="Next page"
              disabled={page >= totalPages || schoolsLoading}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              sx={{
                width: 28,
                height: 28,
                color: "#6E6893",
                borderRadius: "7px",
              }}
            >
              <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Typography
              sx={{
                color: "#292948",
                fontSize: 12,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                letterSpacing: 0.6,
                ml: 0.5,
              }}
            >
              Total - {formatNumber(totalRecords)}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <AddSchoolModal
        open={addModalOpen}
        submitting={creating}
        initialValues={editingSchool}
        title={editingSchool ? "Edit School" : "Add a School"}
        submitLabel={editingSchool ? "Save" : "Add"}
        onClose={handleCloseSchoolModal}
        onSubmit={handleSubmitSchool}
      />
    </AdminDashboardLayout>
  );
};

export default SchoolsPage;
